import { Goal, ID, PrefixedID, Tag, Task } from '@griply/types';

import { buildTaskTagsMap } from './build-task-tags-map';
import { normalizeForSearch } from './normalize-for-search';

export type FilterTasksBySearchQueryOptions = {
  goals: Goal[];
  tags: Tag[];
  relationshipsMap: Record<PrefixedID, PrefixedID[]>;
};

export const filterTasksBySearchQuery = (
  tasks: Task[],
  searchQuery: string,
  { goals, tags, relationshipsMap }: FilterTasksBySearchQueryOptions,
): Task[] => {
  const normalizedQuery = normalizeForSearch(searchQuery.trim());
  if (!normalizedQuery) {
    return tasks;
  }

  const matchingGoalIds = new Set(
    goals
      .filter((goal) =>
        normalizeForSearch(goal.name ?? '').includes(normalizedQuery),
      )
      .map((goal) => goal.id),
  );
  const matchingTagIds = new Set(
    tags
      .filter((tag) => normalizeForSearch(tag.name).includes(normalizedQuery))
      .map((tag) => tag.id),
  );
  const taskTagsMap = buildTaskTagsMap(
    tasks.map((task) => task.id),
    relationshipsMap,
  );

  const matchesTag = (taskId: ID) => {
    if (!matchingTagIds.size) {
      return false;
    }
    return (taskTagsMap.get(taskId) ?? []).some((tagId) =>
      matchingTagIds.has(tagId),
    );
  };

  return tasks.filter((task) => {
    if (normalizeForSearch(task.name ?? '').includes(normalizedQuery)) {
      return true;
    }
    if (
      task.description &&
      normalizeForSearch(task.description).includes(normalizedQuery)
    ) {
      return true;
    }
    if (task.goalId && matchingGoalIds.has(task.goalId)) {
      return true;
    }
    return matchesTag(task.id);
  });
};
